import React, { useEffect, useRef, useState } from "react";
import { motion } from 'framer-motion';
import { Fade } from "react-awesome-reveal";
import { MdChevronRight } from "react-icons/md";

import "../../styling.css";
import BackgroundCover from "../../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../../components/TitleWithDescription/TitleWithDescription";
import { debounce, modifyStyles, resetDefaultStyles } from "../../../utils/utilFunctions";
import { isMobile } from "../../../utils/appUtils";
import { COMPONENT_COLORS } from '../../../utils/componentColors';
import Title from "../../../components/Title/Title";
import Collection from "../../../components/Collection/Collection";
import { getLocalizedJewelleryCollectionItems } from "../../../utils/jewelleryCollectionUtils";
import { useLanguage } from "../../../context/LanguageContext";

function ChineseZodiacCollection() {
    const { language } = useLanguage();
    const [markdownContent, setMarkdownContent] = useState('');
    const colorFont = COMPONENT_COLORS.TEXT.LIGHT;
    const defaultColorFont = COMPONENT_COLORS.TEXT.DARK;
    const backgroundImageRef = useRef(null);

    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const staggerChildren = {
        animate: {
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    useEffect(() => {
        const fetchMarkdownContent = async () => {
            try {
                const response = await fetch('/snake.md');
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                const text = await response.text();
                setMarkdownContent(text);
            } catch (error) {
                console.error('Error fetching markdown:', error);
                setMarkdownContent('Failed to load content. Please try again later.');
            }
        };

        fetchMarkdownContent();
    }, []);

    useEffect(() => {
        const headerEl: HTMLElement | null = document.querySelector(".header");
        const tabsEl: HTMLElement | null = document.querySelector(".tabs");
        const tabContainerDivEl: HTMLElement | null = document.querySelector(".tabs-container");
        const watchContainerEl: HTMLElement | null = document.querySelector(".watch-container");

        const backgroundImageDiv = backgroundImageRef.current;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    (window as any).isIntersectingBg = true;
                } else {
                    (window as any).isIntersectingBg = false;
                }
            });
        });

        if (backgroundImageDiv) {
            observer.observe(backgroundImageDiv);
        }

        return () => {
            (window as any).isIntersectingBg = undefined;
            resetDefaultStyles(headerEl, tabsEl, watchContainerEl, tabContainerDivEl, colorFont, defaultColorFont);
            if (backgroundImageDiv) {
                observer.unobserve(backgroundImageDiv);
            }
        };
    }, [colorFont, defaultColorFont]);

    const getSection = (sectionName: string): string => {
        const sectionRegex = new RegExp(`## ${sectionName}([\\s\\S]*?)(?=## |$)`, 'i');
        const match = markdownContent.match(sectionRegex);
        return match ? match[1].trim() : '';
    };

    const renderContent = () => {
        const fadeDuration = 2000;
        const collectionItemsForGrid = getLocalizedJewelleryCollectionItems('chinese_zodiacs', language);

        return (
            <>
                {/* Tourmaline Frequencies */}
                <div className="py-8">
                    <TitleWithDescription
                        title={{
                            firstLine: "CHINESE",
                            secondLine: "ZODIAC"
                        }}
                        description={`The circle, symbol of the totality. Strength from within. The one who
lives within is not hit by the storm outside. Detachment is the master of independence.`}
                        layout="row"
                        className="px-48"
                    />
                </div>

                {/* Crystals in Symphony */}
                <TitleWithDescription
                    title={{
                        firstLine: "CRYSTALS IN",
                        secondLine: "SYMPHONY"
                    }}
                    description={`After two decades of research, we have perfected the fusion of materials in ways never before seen in watchmaking. The indexes are extracted from the dial and immersed in the watch glass, seemingly floating in the air - a physical manifestation of elevated consciousness. discover more.`}
                    layout="column"
                    image={{
                        src: "/images/jewellery_zodiac_placeholder.png",
                        alt: "Chinese Zodiac jewelry piece",
                        position: "left"
                    }}
                    link={{
                        text: "discover more",
                        onClick: () => console.log("Navigate to discover more")
                    }}
                    className="bg-gradient-to-b from-[#D4DDE4] via-[#C9D3DC] to-[#BAC8D4] px-48"
                />

                {/* The Collection intro */}
                <TitleWithDescription
                    title={{
                        firstLine: "PLACEHOLDER",
                        secondLine: "Re: COLLECTION"
                    }}
                    description="We believe a watch is more than just a time keeper: We create  watches that reflect who you really are, who you want to be."
                    layout="row"
                    className="px-48"
                />

                {/* The Collection */}
                <div className="pb-16">
                    <Collection
                        // title="THE COLLECTION"
                        items={collectionItemsForGrid}
                        collectionId="chinese_zodiacs"
                        buildItemLink={(item) => `/jewellery/collections/chinese_zodiacs/${item.id}`}
                    />
                </div>
            </>
        );
    };

    return (
        <div className="min-h-screen bg-white">
            <BackgroundCover
                backgroundHeight={isMobile() ? 400 : undefined}
                imagePosition="center"
                backgroundImage="/images/jewellery_zodiac_bg.png"
                isWatchPage={true}
            >
                <div className="absolute top-1/2 left-[10%] -translate-y-1/2 z-10">
                    <Title
                        firstLine="YEAR OF"
                        secondLine="THE SNAKE"
                        className="text-white hidden md:block"
                        animate={true}
                    />
                </div>
            </BackgroundCover>
            <div className="w-full">
                {renderContent()}
            </div>
        </div>
    );
}

export default ChineseZodiacCollection;
